import React, { useEffect, useState } from "react";
import { Sparkles, Loader2, Wand2, CheckCircle2, Zap } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";

const SCOPE_SETS = {
  visuals: new Set(["visuals", "lighting", "materials", "vfx", "environment", "colour", "post", "ui"]),
  gameplay: new Set(["gameplay", "pacing", "controls", "combat", "feedback"]),
  animation: new Set(["animation", "movement", "transitions"]),
  audio: new Set(["audio", "sound", "music"]),
};
const SCOPE_BTNS = [
  { id: "everything", label: "Everything", emoji: "🌟" },
  { id: "visuals", label: "Visuals", emoji: "🎨" },
  { id: "gameplay", label: "Gameplay", emoji: "🎮" },
  { id: "animation", label: "Animation", emoji: "🕺" },
  { id: "performance", label: "Perf-safe", emoji: "⚡" },
  { id: "customise", label: "Custom", emoji: "🎛️" },
];
const DISC_COLOR = (d) => {
  if (SCOPE_SETS.visuals.has(d)) return "text-fuchsia-300 border-fuchsia-500/40 bg-fuchsia-500/10";
  if (SCOPE_SETS.gameplay.has(d)) return "text-sky-300 border-sky-500/40 bg-sky-500/10";
  if (SCOPE_SETS.animation.has(d)) return "text-emerald-300 border-emerald-500/40 bg-emerald-500/10";
  if (SCOPE_SETS.audio.has(d)) return "text-amber-300 border-amber-500/40 bg-amber-500/10";
  return "text-white/60 border-white/15 bg-white/10";
};

// Reusable "✨ Make It Amazing" dialog — analyse a target, pick improvements, apply via the
// Safe AI Change Engine. Works from the builder toolbar (whole game) and the inspector (a part).
export default function MakeItAmazingDialog({ gameId, open, onOpenChange, defaultTarget = "game", subjectLabel, onApplied }) {
  const [targets, setTargets] = useState([{ id: "game", label: "Whole game" }]);
  const [target, setTarget] = useState(defaultTarget);
  const [busy, setBusy] = useState(false);
  const [plan, setPlan] = useState(null);
  const [sel, setSel] = useState(new Set());
  const [applying, setApplying] = useState(false);
  const [done, setDone] = useState(null);

  useEffect(() => { api.get("/visual/quality-targets").then((r) => setTargets(r.data.amazing_targets || [])).catch(() => {}); }, []);
  useEffect(() => { if (open) { setTarget(defaultTarget); setPlan(null); setSel(new Set()); setDone(null); } }, [open, defaultTarget]);

  const analyse = async () => {
    setBusy(true); setPlan(null); setSel(new Set()); setDone(null);
    try {
      const { data } = await api.post(`/visual/${gameId}/amazing/plan`, { target, focus: "everything" }, { timeout: 150000 });
      setPlan(data);
      setSel(new Set((data.improvements || []).map((i) => i.id)));
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't draft a plan.");
    } finally { setBusy(false); }
  };

  const applyScope = (scope) => {
    if (!plan) return;
    if (scope === "customise") { setSel(new Set()); return; }
    const imps = plan.improvements || [];
    let ids;
    if (scope === "everything") ids = imps.map((i) => i.id);
    else if (scope === "performance") ids = imps.filter((i) => !i.heavy).map((i) => i.id);
    else { const s = SCOPE_SETS[scope] || new Set(); ids = imps.filter((i) => s.has(i.discipline)).map((i) => i.id); }
    setSel(new Set(ids));
  };
  const toggle = (id) => setSel((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const apply = async () => {
    const chosen = (plan?.improvements || []).filter((i) => sel.has(i.id));
    if (!chosen.length) { toast.error("Select at least one improvement."); return; }
    setApplying(true);
    try {
      const { data } = await api.post(`/visual/${gameId}/amazing/apply`, {
        instructions: chosen.map((i) => i.instruction),
        summary: `Make It Amazing — ${plan.target_label || "game"} (${chosen.length})`,
      }, { timeout: 150000 });
      if (data.changed) {
        setDone({ version: data.version, applied: data.applied });
        toast.success(`Applied ${data.applied} improvement${data.applied === 1 ? "" : "s"} — saved as v${data.version} (undoable).`);
        if (onApplied && data.config) onApplied(data.config);
      } else toast.message(data.summary || "No safe change could be applied.");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't apply improvements.");
    } finally { setApplying(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg bg-[#0a0d18] border-white/10 text-white" data-testid="amazing-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-display">
            <span className="grid place-items-center w-7 h-7 rounded-lg bg-gradient-to-br from-fuchsia-500 to-amber-400 text-black"><Sparkles className="w-4 h-4" /></span>
            Make It Amazing
          </DialogTitle>
          <DialogDescription className="text-white/50">
            {subjectLabel ? `Improve ${subjectLabel} across visuals, feel & sound.` : "The AI proposes a plan across visuals, lighting, animation, sound & feel. Applied changes are snapshotted & undoable."}
          </DialogDescription>
        </DialogHeader>

        {!plan && (
          <div className="flex flex-wrap items-center gap-2">
            <select value={target} onChange={(e) => setTarget(e.target.value)} data-testid="amazing-dialog-target"
              className="rounded-lg bg-black/40 border border-white/15 px-3 py-2 text-sm text-white outline-none">
              {targets.map((t) => <option key={t.id} value={t.id} className="bg-[#0b0f1c]">{t.label}</option>)}
            </select>
            <Button onClick={analyse} disabled={busy} data-testid="amazing-dialog-analyse"
              className="rounded-full bg-gradient-to-r from-fuchsia-500 to-amber-400 hover:opacity-90 text-black font-bold">
              {busy ? <><Loader2 className="w-4 h-4 animate-spin" /> Analysing…</> : <><Sparkles className="w-4 h-4" /> Analyse & improve</>}
            </Button>
          </div>
        )}

        {plan && (
          <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-1">
            <div className="flex items-start gap-2">
              <Wand2 className="w-4 h-4 text-fuchsia-300 mt-0.5 shrink-0" />
              <div>
                <p className="text-sm font-semibold" data-testid="amazing-dialog-count">{(plan.improvements || []).length} improvements found</p>
                <p className="text-xs text-white/50">{plan.overview}</p>
              </div>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {SCOPE_BTNS.map((s) => (
                <button key={s.id} onClick={() => applyScope(s.id)} data-testid={`amazing-dialog-scope-${s.id}`}
                  className="inline-flex items-center gap-1 rounded-full border border-white/12 bg-white/5 px-2.5 py-1 text-[11px] font-semibold text-white/70 hover:bg-white/10">
                  <span>{s.emoji}</span> {s.label}
                </button>
              ))}
            </div>
            <div className="space-y-1.5">
              {(plan.improvements || []).map((imp) => (
                <label key={imp.id} className={`flex items-start gap-2.5 rounded-xl border px-3 py-2 cursor-pointer transition-colors ${sel.has(imp.id) ? "border-fuchsia-500/40 bg-fuchsia-500/[0.06]" : "border-white/8 bg-black/20 hover:bg-white/[0.03]"}`}>
                  <input type="checkbox" checked={sel.has(imp.id)} onChange={() => toggle(imp.id)} className="mt-1 accent-fuchsia-500 w-4 h-4" />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <span className="text-sm font-semibold text-white/90">{imp.title}</span>
                      <span className={`text-[9px] uppercase tracking-wide rounded-full px-1.5 py-0.5 border ${DISC_COLOR(imp.discipline)}`}>{imp.discipline}</span>
                      {imp.heavy && <span className="text-[9px] rounded-full px-1.5 py-0.5 border border-amber-500/40 bg-amber-500/10 text-amber-300 inline-flex items-center gap-0.5"><Zap className="w-2.5 h-2.5" />heavy</span>}
                    </div>
                    <p className="text-[11px] text-white/45 mt-0.5">{imp.rationale}</p>
                  </div>
                </label>
              ))}
            </div>
            <div className="flex items-center gap-3 pt-1">
              <Button onClick={apply} disabled={applying || sel.size === 0} data-testid="amazing-dialog-apply"
                className="rounded-full bg-emerald-500 hover:bg-emerald-600 text-black font-bold">
                {applying ? <><Loader2 className="w-4 h-4 animate-spin" /> Applying…</> : <><CheckCircle2 className="w-4 h-4" /> Apply ({sel.size})</>}
              </Button>
              {done && <span className="text-xs text-emerald-300 inline-flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5" /> Saved v{done.version}</span>}
              <Button variant="ghost" size="sm" onClick={analyse} disabled={busy} className="ml-auto text-white/50 hover:text-white text-xs">Re-analyse</Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
